import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { FunctionComponent, useState } from "react"
import { InputOutlined, TextAreaOutlined } from "@nova/ds-component-input"
import { UploadDropBox } from "panels/content/side-panel/upload-flow/components/UploadDropBox"
import { FileExtension } from "@nova/constants"
import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { useCreateAircraftFile, aircraftFileS3Key } from "./AircraftFileQueries"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { v4 } from "uuid"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"

interface UploadAircraftFileModalProps {
    onClose: () => void
    onSubmit: () => void
    aircraftId: string
}

const UploadAircraftFileModal: FunctionComponent<UploadAircraftFileModalProps> = ({
    onClose,
    onSubmit,
    aircraftId,
}) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const { createAircraftFile, isCreating } = useCreateAircraftFile(aircraftId)

    const [name, setName] = useState("")
    const [expiresAt, setExpiresAt] = useState("")
    const [notes, setNotes] = useState("")
    const [file, setFile] = useState<File | null>(null)
    const [isUploading, setIsUploading] = useState(false)

    const canUpload = !isCreating && !isUploading && file && orgId

    const handleSubmit = async () => {
        if (!canUpload) return

        setIsUploading(true)
        try {
            const fileId = v4()

            await fileManagementApi.putFile(file, {
                target: aircraftFileS3Key(orgId, aircraftId, fileId),
            })

            const record = await createAircraftFile({
                id: fileId,
                name: name || file.name,
                expires_at: expiresAt || null,
                notes: notes || null,
                file_name: file.name,
                file_size_bytes: file.size,
            })

            if (!record?.id) {
                throw new Error("Failed to create aircraft file record")
            }

            onSubmit()
        } catch (error) {
            console.error("Upload failed:", error)
        } finally {
            setIsUploading(false)
        }
    }

    return (
        <HighPriorityNovaModalContainer
            className="max-w-[480px] w-full"
            title="Upload File"
            closeModal={onClose}
            bottomBar={
                <div className="flex justify-end gap-2 w-full">
                    <ButtonTertiary
                        title="Cancel"
                        onClick={onClose}
                        disabled={isCreating || isUploading}
                    />
                    <ButtonPrimary
                        title="Upload File"
                        onClick={handleSubmit}
                        disabled={!canUpload}
                    />
                </div>
            }
        >
            <div className="flex flex-col gap-4 pt-2">
                <InputOutlined
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                />

                <div className="w-1/2">
                    <InputOutlined
                        type="date"
                        label="Date Expires"
                        value={expiresAt}
                        onChange={(e) => setExpiresAt(e.currentTarget.value)}
                    />
                </div>

                <TextAreaOutlined
                    label="Notes"
                    value={notes}
                    onChange={(e) => setNotes(e.currentTarget.value)}
                    rows={5}
                />

                <UploadDropBox
                    allowedFileTypes={[
                        FileExtension.pdf,
                        FileExtension.png,
                        FileExtension.jpg,
                        FileExtension.jpeg,
                    ]}
                    onNewItems={async (files) => setFile(files[0])}
                    isProcessing={isCreating || isUploading}
                    title={file ? file.name : "Upload a File"}
                    supportedMimeTypes={[]}
                />
            </div>
        </HighPriorityNovaModalContainer>
    )
}

export { UploadAircraftFileModal }
